import { FiCheck, FiCopy, FiEdit3, FiLink2, FiTrash2 } from 'react-icons/fi';
import { SmallBodyRegular, SmallBodyRegularSemi } from 'components/Text';
import {
    useCountryParam,
    useMutateWatchlistItems,
    useMutateWatchlists,
} from 'utils/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import IconButton from 'components/Button/IconButton';
import { MenuItem } from 'components/Button/MenuButton';
import bugsnagClient from '@/ui/containers/BugsnagClient';
import { forwardRef } from 'react';
import { isSpateUser } from 'utils/helpers';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

const wrapOnClick = (props, onClick) => (e) => {
    onClick(e);
    if (typeof props.onClick === 'function') {
        props.onClick(e);
    }
};

const EditIcon = styled(FiEdit3)`
    color: ${({ theme }) => theme.purple_1};
`;

export const EditButton = forwardRef(
    ({ watchlist, handleMenuClick, ...props }, ref) => {
        return (
            <MenuItem
                IconLeft={EditIcon}
                mixpanelContext="EDIT_WATCHLIST"
                {...props}
                onClick={wrapOnClick(props, handleMenuClick)}
                ref={ref}
            >
                Edit Details
            </MenuItem>
        );
    },
);

const ShareLinkIcon = styled(FiLink2)`
    color: ${({ theme }) => theme.purple_1};
`;

export const PublishButton = forwardRef(
    ({ watchlistId, handleMenuClick, ...props }, ref) => {
        const isAdmin = useSelector(
            ({ session }) =>
                session &&
                isSpateUser({
                    company: session.company,
                    email: session.user_email,
                }),
        );

        if (!isAdmin) return null;

        return (
            <MenuItem
                {...props}
                IconLeft={ShareLinkIcon}
                onClick={wrapOnClick(props, handleMenuClick)}
                mixpanelContext="PUBLISH_WATCHLIST"
                ref={ref}
            >
                Share Link
            </MenuItem>
        );
    },
);

const DuplicateIcon = styled(FiCopy)`
    color: ${({ theme }) => theme.purple_1};
`;

export const SaveAsButton = forwardRef(
    ({ watchlistItems, watchlist: { name, description }, ...props }, ref) => {
        const history = useHistory();
        const { add: addWatchlist } = useMutateWatchlists();
        const { add: addItems } = useMutateWatchlistItems();
        const { pathname, search, hash } = useLocation();
        const { mutateToWithCountryParam } = useCountryParam();

        return (
            <MenuItem
                {...props}
                IconLeft={DuplicateIcon}
                mixpanelContext="DUPLICATE_WATCHLIST"
                onClick={wrapOnClick(props, (e) => {
                    const newId = uuid();
                    Promise.all([
                        addWatchlist({
                            id: newId,
                            data: {
                                name: `${name} (copy)`,
                                description,
                            },
                        }),
                        addItems(
                            watchlistItems.map(
                                ({
                                    type,
                                    name,
                                    categoryId,
                                    brandId,
                                    relatedCategoryId,
                                    affinityId,
                                    ...item
                                }) => {
                                    const data = { type, name };
                                    if (categoryId)
                                        data.categoryId = categoryId;
                                    if (brandId) data.brandId = brandId;
                                    if (relatedCategoryId)
                                        data.relatedCategoryId =
                                            relatedCategoryId;
                                    if (affinityId)
                                        data.affinityId = affinityId;
                                    return { ...item, data, watchlist: newId };
                                },
                            ),
                        ),
                        Promise.resolve(
                            history.push(
                                mutateToWithCountryParam(`/watchlist/${newId}`),
                            ),
                        ),
                    ]).catch((err) => {
                        history.push(`${pathname}${search}${hash}`);
                        toast.error(
                            `There was an issue creating a copy of this watchlist${
                                err && err.message ? `: ${err.message}` : ''
                            }`,
                        );
                        bugsnagClient.notify(err);
                    });
                })}
            >
                Duplicate
            </MenuItem>
        );
    },
);

const MenuSeparator = styled.div`
    min-height: 1px;
    background-color: ${({ theme }) => theme.gray_5};
`;

const DeleteIcon = styled(FiTrash2)`
    color: ${({ theme }) => theme.red_1};
`;

export const DeleteMenuItem = forwardRef(
    ({ watchlist, handleMenuClick, ...props }, ref) => {
        return (
            <>
                <MenuSeparator />
                <MenuItem
                    IconLeft={DeleteIcon}
                    {...props}
                    onClick={wrapOnClick(props, handleMenuClick)}
                    mixpanelContext="DELETE_WATCHLIST"
                    ref={ref}
                >
                    Delete
                </MenuItem>
            </>
        );
    },
);

const HitButton = styled(IconButton)`
    background-color: ${({ theme }) => theme.gray_6};
    border: 1px solid ${({ theme }) => theme.gray_5};
    color: ${({ theme }) => theme.gray_1};
    font-weight: ${({ theme }) => theme.fontMedium};
    text-transform: ${({ $lowercase }) => ($lowercase ? 'none' : '')};
`;

const PurpleCheck = styled(FiCheck)`
    color: ${({ theme }) => theme.purple_1};
`;

export const getWatchlistRenderHits = ({
    watchlistItems,
    onAddItem,
    onRemoveItem,
    dataSource,
}) => {
    if (!(onAddItem && onRemoveItem)) return;
    return function ({ objectID, parentName, ...node }) {
        const currentHashtagType =
            dataSource === 'tiktok' ? 'hashtag' : 'instagram-hashtag';
        const inWatchlist = watchlistItems.find((i) =>
            i.type === currentHashtagType
                ? node.name === i.name
                : i.id === node.id,
        );
        const isTwoTextLineButton =
            node.type !== 'brand' &&
            node.type !== 'category' &&
            node.type !== 'hashtag';
        return (
            <HitButton
                IconLeft={inWatchlist ? PurpleCheck : null}
                onClick={() =>
                    inWatchlist ? onRemoveItem(node) : onAddItem(node)
                }
                key={objectID}
                mixpanelContext="AddBySearchResult"
                $lowercase={node.type === 'hashtag'}
            >
                {isTwoTextLineButton ? (
                    <div css="display: flex; flex-direction: column;">
                        <SmallBodyRegular>
                            {node.brandName || node.categoryName}
                        </SmallBodyRegular>
                        <SmallBodyRegularSemi>{node.name}</SmallBodyRegularSemi>
                    </div>
                ) : (
                    <>
                        {parentName && `${parentName} > `}
                        {node.name}
                    </>
                )}
            </HitButton>
        );
    };
};
